(() => {
  const normalize = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/%20/g, "")
      .replace(/[_\-\s]+/g, "")
      .replace(/[^a-z0-9]/g, "");

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function getColorSelection(root) {
    const fieldsets = Array.from(root.querySelectorAll("fieldset"));

    for (const fieldset of fieldsets) {
      const legend =
        fieldset.querySelector("legend")?.textContent || "";

      const radios = Array.from(
        fieldset.querySelectorAll('input[type="radio"]')
      );

      const isColor =
        /colou?r/i.test(legend) ||
        radios.some((input) => /colou?r/i.test(input.name || ""));

      if (isColor && radios.length) {
        const selected = radios.find((input) => input.checked);

        return {
          selected: selected?.value || "",
          colors: radios.map((input) => input.value).filter(Boolean),
        };
      }
    }

    const selects = Array.from(root.querySelectorAll("select"));

    for (const select of selects) {
      const label =
        root.querySelector(`label[for="${select.id}"]`)?.textContent || "";

      if (
        /colou?r/i.test(label) ||
        /colou?r/i.test(select.name || "")
      ) {
        return {
          selected: select.value,
          colors: Array.from(select.options)
            .map((option) => option.value)
            .filter(Boolean),
        };
      }
    }

    return {
      selected: "",
      colors: [],
    };
  }

  function getFilename(slide) {
    const image = slide.querySelector("img");

    if (!image) return "";

    const src =
      image.currentSrc ||
      image.getAttribute("src") ||
      image.getAttribute("data-src") ||
      "";

    try {
      return normalize(decodeURIComponent(src.split("?")[0]));
    } catch (error) {
      return normalize(src.split("?")[0]);
    }
  }

  function detectSlideColor(slide, colors) {
    const filename = getFilename(slide);

    for (const color of colors) {
      const colorKey = normalize(color);

      // Your filenames contain:
      // color_White
      // color_Cobalt_Blue
      if (filename.includes(`color${colorKey}`)) {
        return colorKey;
      }
    }

    // Images without a color name stay visible for every color.
    return "shared";
  }

  async function initializeGallery(gallery) {
    if (gallery.dataset.mhColorGalleryReady === "true") return;

    const carousel = gallery.querySelector("gallery-carousel");

    if (!carousel) return;

    /*
     * Nimbus creates carousel.main and carousel.thumbnails
     * after the custom element initializes.
     */
    let attempts = 0;

    while (!carousel.main && attempts < 50) {
      await wait(100);
      attempts += 1;
    }

    if (!carousel.main) return;

    const mainList = carousel.querySelector(
      ".main-carousel .splide__list"
    );

    const thumbnailList = carousel.querySelector(
      ".thumbnail-carousel .splide__list"
    );

    if (!mainList) return;

    /*
     * Keep the original DOM elements in memory.
     * We will move them in/out instead of creating duplicates.
     */
    const originalMainSlides = Array.from(
      mainList.children
    ).filter((element) =>
      element.classList.contains("splide__slide")
    );

    const originalThumbnailSlides = thumbnailList
      ? Array.from(thumbnailList.children).filter((element) =>
          element.classList.contains("splide__slide")
        )
      : [];

    const productRoot =
      gallery.closest("section") ||
      gallery.parentElement ||
      document;

    function rebuildGallery() {
      const { selected, colors } =
        getColorSelection(productRoot);

      if (!selected || colors.length === 0) return;

      const selectedKey = normalize(selected);

      const matchingMainSlides = originalMainSlides.filter(
        (slide) => {
          const slideColor =
            detectSlideColor(slide, colors);

          return (
            slideColor === "shared" ||
            slideColor === selectedKey
          );
        }
      );

      /*
       * Safety:
       * Never empty the gallery if filenames don't match.
       */
      const actualColorMatches = originalMainSlides.filter(
        (slide) =>
          detectSlideColor(slide, colors) === selectedKey
      );

      if (actualColorMatches.length === 0) {
        console.warn(
          "MH Color Gallery: no images found for",
          selected
        );
        return;
      }

      const allowedMediaIds = new Set(
        matchingMainSlides
          .map((slide) =>
            slide.getAttribute("data-media-id")
          )
          .filter(Boolean)
      );

      const matchingThumbnailSlides =
        originalThumbnailSlides.filter((slide) =>
          allowedMediaIds.has(
            slide.getAttribute("data-media-id")
          )
        );

      /*
       * Physically remove the other-color slides.
       * This is what fixes mobile.
       */
      mainList.replaceChildren(...matchingMainSlides);

      if (thumbnailList) {
        thumbnailList.replaceChildren(
          ...matchingThumbnailSlides
        );
      }

      /*
       * Tell Nimbus/Splide that the slide structure changed.
       */
      carousel.main.refresh();

      if (carousel.thumbnails) {
        carousel.thumbnails.refresh();
      }

      requestAnimationFrame(() => {
        carousel.main.go(0);

        if (carousel.thumbnails) {
          carousel.thumbnails.go(0);
        }

        if (
          typeof carousel.updateSlideCounter === "function"
        ) {
          carousel.updateSlideCounter(0);
        }
      });
    }

    productRoot.addEventListener("change", (event) => {
      if (
        event.target.matches(
          'input[type="radio"], select'
        )
      ) {
        setTimeout(rebuildGallery, 100);
      }
    });

    productRoot.addEventListener("click", (event) => {
      if (event.target.closest("label")) {
        setTimeout(rebuildGallery, 150);
      }
    });

    gallery.dataset.mhColorGalleryReady = "true";

    setTimeout(rebuildGallery, 250);
    setTimeout(rebuildGallery, 700);
  }

  function initializeAll(scope = document) {
    scope
      .querySelectorAll(
        'media-gallery[id^="MediaGallery-"]'
      )
      .forEach(initializeGallery);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initializeAll()
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => initializeAll(event.target)
  );
})();